/**
 * Tax Audit Log Page
 * View audit trail of financial changes for compliance
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Shield,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';

interface AuditLogEntry {
  id: string;
  userId: string | null;
  userName: string | null;
  action: string;
  tableName: string;
  recordId: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

const TABLE_FILTERS = [
  { value: 'all', label: 'All Tables' },
  { value: 'biz_invoices', label: 'Invoices' },
  { value: 'biz_payments', label: 'Payments' },
  { value: 'biz_expenses', label: 'Expenses' },
  { value: 'biz_subscriptions', label: 'Subscriptions' },
  { value: 'biz_contracts', label: 'Contracts' },
  { value: 'biz_business_profiles', label: 'Business Profiles' },
  { value: 'biz_budgets', label: 'Budgets' },
  { value: 'biz_recurring_schedules', label: 'Recurring Schedules' },
];

const ACTION_COLORS: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'info'> = {
  INSERT: 'success',
  UPDATE: 'warning',
  DELETE: 'destructive',
};

export function AuditLogPage() {
  const [tableName, setTableName] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['tax-audit-log', tableName, dateFrom, dateTo, page],
    queryFn: async () => {
      const result = await api.getTaxAuditLog({
        tableName: tableName !== 'all' ? tableName : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        limit,
      });
      return {
        logs: (result.data || []) as AuditLogEntry[],
        pagination: result.pagination || { page: 1, limit, total: 0, totalPages: 0 },
      };
    },
  });

  const logs = data?.logs || [];
  const pagination = data?.pagination || { page: 1, limit, total: 0, totalPages: 0 };

  const formatTableName = (name: string) => {
    return name.replace('biz_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Audit Log</h1>
        <p className="text-muted-foreground">Complete audit trail of financial data changes for compliance</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Select value={tableName} onValueChange={(v) => { setTableName(v); setPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by table..." />
                </SelectTrigger>
                <SelectContent>
                  {TABLE_FILTERS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                placeholder="From date"
              />
            </div>
            <div>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                placeholder="To date"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => { setTableName('all'); setDateFrom(''); setDateTo(''); setPage(1); }}
            >
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Audit Trail
            <span className="text-sm font-normal text-muted-foreground">
              ({pagination.total} records)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No audit log entries found</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant={ACTION_COLORS[log.action] || 'secondary'} className="w-16 justify-center">
                        {log.action}
                      </Badge>
                      <div className="min-w-0">
                        <div className="font-medium">{formatTableName(log.tableName)}</div>
                        <div className="text-xs text-muted-foreground">
                          {log.userName || 'System'} &middot; {formatDateTime(log.createdAt)}
                          {log.ipAddress && ` &middot; ${log.ipAddress}`}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pagination.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Badge variant={ACTION_COLORS[selectedLog?.action || ''] || 'secondary'}>
                {selectedLog?.action}
              </Badge>
              {selectedLog && formatTableName(selectedLog.tableName)}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">User:</span>{' '}
                  <span className="font-medium">{selectedLog.userName || 'System'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Date:</span>{' '}
                  <span className="font-medium">{formatDateTime(selectedLog.createdAt)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Record ID:</span>{' '}
                  <span className="font-mono text-xs">{selectedLog.recordId || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">IP:</span>{' '}
                  <span className="font-medium">{selectedLog.ipAddress || '-'}</span>
                </div>
              </div>

              {selectedLog.oldValues && (
                <div>
                  <h4 className="font-medium mb-2 text-sm text-muted-foreground">Previous Values</h4>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-48">
                    {JSON.stringify(selectedLog.oldValues, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.newValues && (
                <div>
                  <h4 className="font-medium mb-2 text-sm text-muted-foreground">New Values</h4>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-48">
                    {JSON.stringify(selectedLog.newValues, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
